import { useCallback, Dispatch } from 'react';

import endpoints from '../../../constants/configs/endpoints';
import { api } from '../../../modules/common-utils-services/axiosUtil';
import { isSameMonthAndYear } from '../utils/date.utils';
import { adjustUTCToLocalTime } from '../../../utils/dates/dateMethods.js'
import { ENROLLMENT_PLAN_STATUS } from '../myEnrollment/enrollmentHistoryTypes';
import { PLAN_TYPE } from '../../../modules/custom-grouping/modals/modalTypes';
import { IEnrolleeDetails, IEnrollmentDetails } from '../myEnrollment/enrollmentHistoryTypes';
import { RELATIONSHIP_TYPES } from '../../../constants/common/relationShipTypes';
import { IDisernrollParams } from '../myEnrollment/ActionButtonContainer';
import { ILocalDates } from '../../../utils/types/environment';
import { startSpinner, stopSpinner } from '../../../actions/common/actionCreators';
import { SPINNER_MESSAGES_IDS } from '../../../constants/common/miscellaneous';
import { getEnRollmentHistory } from '../../../actions/individualPortal/enrollmentHistory/enrollmentHistoryActionCreators';

interface IpayForPlanParams {
    caseNumber: string;
    typeOfPlan: PLAN_TYPE;
    enrollmentId: string
}

const subscriberRelationship = [RELATIONSHIP_TYPES.SELF.value];

export const LOGO_URLS = {
    'HEALTH': global?.__ENV__?.PRODUCTION ? '/hix/resources/img/health-icon.png' : '/assets/images/health-icon.png',
    'DENTAL': global?.__ENV__?.PRODUCTION ? '/hix/resources/img/tooth-icon.png' : '/assets/images/tooth-icon.png',
};

export default (enrollmentDetails: IEnrollmentDetails, dispatch: Dispatch<any>) => {

    const getTerminationDates = (disernrollParams: IDisernrollParams, coverageStartDateForDisenrollment: string) => {
        // Filter enrollees based on memberGuidsToDisenroll
        const disenrolledMembers = enrollmentDetails.enrolleeHistoryEntries.filter(enrollee => disernrollParams.memberGuidsToDisenroll.includes(enrollee.exchgIndivIdentifier));

        //Eliminate an object from the disenrolledMembers array if there are two objects with the same exchgIndivIdentifier and one object's endDate is before serverDate,
        const filteredDisenrolledMembers = disenrolledMembers.filter((member, index, array) => {
            const matchingMembers = array.filter(item => item.exchgIndivIdentifier === member.exchgIndivIdentifier);
            const endDate = new Date(member.endDate);
            return endDate >= new Date(coverageStartDateForDisenrollment) || matchingMembers.length === 1;
        });

        // Find the endDate for objects in enrollees whose exchgIndivIdentifier is in memberGuidsToDisenroll
        const formattedEndDateArray = filteredDisenrolledMembers.map(member => new Date(member.endDate));
        // Find the smallest date from formattedEndDateArray
        const smallestDate = new Date(Math.min.apply(null, formattedEndDateArray));

        let terminationDetails = {
            endOfCurrentMonth: enrollmentDetails.endOfCurrentMonth,
            endOfMonthAfterNext: enrollmentDetails.endOfMonthAfterNext,
            endOfNextMonth: enrollmentDetails.endOfNextMonth
        }
        //Replace values in terminationDetails with an empty string if they are greater than the smallestDate
        Object.keys(terminationDetails).forEach(key => {
            if (terminationDetails[key]) {
                const date = new Date(terminationDetails[key]);
                if (date >= smallestDate) {
                    terminationDetails[key] = "";
                } else {
                    terminationDetails[key] = new Date(terminationDetails[key]).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
                }
            } else {
                terminationDetails[key] = "";
            }
        });
        return terminationDetails
    }

    const checkForPendingApplication = async ({ coverageYear }) => {
        const url = endpoints.CHECK_FOR_PENDING_APPLICATION;
        const params = { coverageYear };
        try {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            const res = global.__ENV__.PRODUCTION ? await api.post(url, params) : await api.get(url);
            return res;
        } catch (e) {
            throw new Error(e);
        } finally {
            dispatch(stopSpinner());
        }
    };

    const getTerminationBtnVisibility = () => {
        return enrollmentDetails.terminationDateChangeAllowed && !isSameMonthAndYear(enrollmentDetails.coverageStartDate, enrollmentDetails.coverageEndDate)

    };

    const getPayNowBtnVisibility = () => {
        let btnVisibility = false;
        if (enrollmentDetails.allowPaymentRedirect && enrollmentDetails.enrollmentStatus !== null && enrollmentDetails.enrollmentStatus.toUpperCase() === ENROLLMENT_PLAN_STATUS.PENDING.value && parseFloat(enrollmentDetails.netPremium) > 0) {
            btnVisibility = true;
        }
        return btnVisibility;
    };

    const payForPlan = async ({ caseNumber, typeOfPlan, enrollmentId }: IpayForPlanParams, openModal: Function) => {
        const params = { 'caseNumber': caseNumber, 'typeOfPlan': typeOfPlan, 'enrollmentId': enrollmentId };
        const url = endpoints.PAY_FOR_HEALTH;
        try {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            const response = global.__ENV__.PRODUCTION ? await api.post(url, params) : await api.get(url);
            if (response.data) {
                openModal('payForEnrollment', { "finEnrlHltDataKey": response.data });
            } else {
                openModal('disenrollFailureModal');
            }
        } catch (e) {
            openModal('disenrollFailureModal');
        }
        finally {
            dispatch(stopSpinner());
        }
    }
    const getDisEnrollBtnVisibility = (localDates: ILocalDates) => {
        /**
         * Determines the visibility of the DisEnroll button.
         * 
         * Conditions:
         * 1. HIX-167985: Button is hidden if all members, except the subscriber, have future coverage start dates.
         * 2. The coverage dates must be valid.
         */

        const showDisEnrollment = validCoverage();
        const isValidDate = validateCoverageStartDate(
            {
                coverageStartDate: new Date(enrollmentDetails.coverageStartDate),
                coverageEndDate: new Date(enrollmentDetails.coverageEndDate),
            },
            localDates
        );

        return isValidDate !== null && isValidDate && showDisEnrollment;
    };

    const validCoverage = () => {
        let showDisEnrollment = false;
        const groupedActiveEntries = enrollmentDetails?.enrolleeHistoryEntries?.reduce((groups, entry) => {
            if (entry.relationship.toUpperCase() !== RELATIONSHIP_TYPES.SELF.value) {
                const groupId = entry.exchgIndivIdentifier;
                if (!groups[groupId]) {
                    groups[groupId] = [];
                }
                groups[groupId].push(entry);
            }
            return groups;
        }, {});

        if (groupedActiveEntries && Object.keys(groupedActiveEntries).length > 0) {
            showDisEnrollment = !Object.values(groupedActiveEntries).every((group: IEnrolleeDetails[]) =>
                group.some(entry => new Date(entry.startDate) > groupedActiveEntries)
            );
        } else {
            showDisEnrollment = true;
        }
        return showDisEnrollment
    }

    const changeTerminationDate = async (params, openModal, coverageYear) => {
        const url = endpoints.CHANGE_TERMINATION_DATE;
        try {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            const response = global.__ENV__.PRODUCTION ? await api.post(url, params) : await api.get(url);
            if (response.data.toUpperCase() === 'SUCCESS') {
                openModal('submitSuccess', {
                    actionLabel: "indportal.enrollments.successful.termination.date",
                    isChangeTerminationDate: true,
                    onModalClose: () => dispatch(getEnRollmentHistory(coverageYear))
                });
            } else {
                openModal('disenrollFailureModal');
            }
        } catch (e) {
            openModal('disenrollFailureModal');
        }
        finally {
            dispatch(stopSpinner());
        }
    };

    const getValidEnrollees = (enrollees, coverageStartDateForDisenrollment) => {
        coverageStartDateForDisenrollment = new Date(coverageStartDateForDisenrollment);
        return enrollees.filter(enrollee => {
            return (
                new Date(enrollee.startDate) <= coverageStartDateForDisenrollment && new Date(enrollee.endDate) >= coverageStartDateForDisenrollment
            );
        });
    }

    const validateTerminationdate = (endDate, coverageStartDateForDisenrollment) => {
        const currentDate = new Date(coverageStartDateForDisenrollment);
        const terminationDate = new Date(endDate);
        return terminationDate.getMonth() < currentDate.getMonth() && terminationDate.getFullYear() <= currentDate.getFullYear()
    }

    const getEnrollees = (coverageStartDateForDisenrollment) => {
        if (!coverageStartDateForDisenrollment) {
            return getValidEnrollees([], coverageStartDateForDisenrollment);
        }

        const enrollees = enrollmentDetails.enrolleeHistoryEntries.map((enrolleeDetails) => {
            const isSubscriber = subscriberRelationship.includes(enrolleeDetails.relationship.toUpperCase());
            const isTerminationValid = validateTerminationdate(enrolleeDetails.endDate, coverageStartDateForDisenrollment);

            return {
                ...enrolleeDetails,
                checked: isSubscriber,
                disable: isTerminationValid,
            };
        });

        return getValidEnrollees(enrollees, coverageStartDateForDisenrollment);
    };

    const getCancelBtnVisibility = (localDates: ILocalDates): boolean => {
        /** Determines the visibility of the Cancel button.
        Conditions:
        Coverage must be valid.
        Coverage start date must not be valid for disenrollment and not null.
        */

        const showDisEnrollment = validCoverage();
        const isValidDate = validateCoverageStartDate(
            {
                coverageStartDate: new Date(enrollmentDetails.coverageStartDate),
                coverageEndDate: new Date(enrollmentDetails.coverageEndDate),
            },
            localDates
        );

        return isValidDate !== null && !isValidDate && showDisEnrollment;
    };

    const submitDisEnrollment = async (iparams: IDisernrollParams, openModal, isCancelCoverage, actionLabel) => {
        let url = '';
        iparams.planType = iparams.planType.toUpperCase();
        if (!iparams.reasonCode) {
            iparams.reasonCode = 'NO_OPTION_SELECTED';
        }
        let params = {};
        if (iparams.planType === PLAN_TYPE.HEALTH) {
            url = iparams.isMemberLevelDisenrollEnabled ? endpoints.DISENROLL_MEMBERS : endpoints.TERMINATE_HEALTH_PLAN;
            params = {
                'reasonCode': iparams.reasonCode,
                'otherReason': iparams.otherReason,
            };
            if (iparams.isMemberLevelDisenrollEnabled) {
                params['caseNumber'] = iparams.caseNumber;
                params['planType'] = PLAN_TYPE.HEALTH;
                params['enrollmentId'] = iparams.enrollmentId;
            } else {
                params['healthEnrollmentId'] = iparams.enrollmentId;
                params['caseNumber'] = iparams.caseNumber;
                params['coverageStartDate'] = enrollmentDetails.coverageStartDate;
                params['isRecTribe'] = false;
            }
        } else {
            url = iparams.isMemberLevelDisenrollEnabled ? endpoints.DISENROLL_MEMBERS : endpoints.TERMINATE_DENTAL_PLAN;
            params = {
                'reasonCode': iparams.reasonCode,
                'otherReason': iparams.otherReason,
            };
            if (iparams.isMemberLevelDisenrollEnabled) {
                params['caseNumber'] = iparams.caseNumber;
                params['planType'] = PLAN_TYPE.DENTAL;
                params['enrollmentId'] = iparams.enrollmentId;
            } else {
                params['dentalEnrollmentId'] = iparams.enrollmentId;
                params['caseNumber'] = iparams.caseNumber;
                params['coverageStartDate'] = enrollmentDetails.coverageStartDate;
                params['isRecTribe'] = false;
            }
        }
        if (!!iparams.terminationDateChoice) {
            params['terminationDateChoice'] = iparams.terminationDateChoice
        }
        if (iparams.isMemberLevelDisenrollEnabled) {
            params['memberGuidsToDisenroll'] = iparams.memberGuidsToDisenroll;
        }

        try {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            const response = global.__ENV__.PRODUCTION ? await api.post(url, params) : await api.get(url);
            if (response.data.toUpperCase() === 'SUCCESS') {
                openModal('submitSuccess', { actionLabel, "isVoluntaryDisEnrollment": true, isCancelCoverage })
            }else {
                openModal('disenrollFailureModal');
            }
        } catch (e) {
            openModal('disenrollFailureModal');
        }
        finally {
            dispatch(stopSpinner());
        }
    };

    const sumSubsidyAndPremiumCredit = (stateSubsidy: string, premiumCredit: string) => {
        var totalStateSubsidy = 0;
        if (stateSubsidy !== null) {
            totalStateSubsidy += parseFloat(stateSubsidy);
        }
        if (premiumCredit !== null) {
            totalStateSubsidy += parseFloat(premiumCredit);
        }
        return totalStateSubsidy;
    };

    const validateCoverageStartDate = (
        { coverageStartDate, coverageEndDate },
        localDates: ILocalDates,
    ): boolean | null => {
        const { serverDateInUTC, dstSavingsMs, timezoneOffsetMs } = localDates || {};
        const { sDate, sTime } = adjustUTCToLocalTime(serverDateInUTC, parseInt(timezoneOffsetMs), parseInt(dstSavingsMs));
        const currentDate = new Date(`${sDate} ${sTime}`);
        if (coverageEndDate) {
            const coverageEndDateFormatted = new Date(coverageEndDate);
            const coverageStartDateFormatted = new Date(coverageStartDate);
            return coverageEndDateFormatted < currentDate ? null : coverageStartDateFormatted <= currentDate;
        } else {
            const coverageStartDateFormatted = new Date(coverageStartDate);
            return coverageStartDateFormatted <= currentDate;
        }
    };

    return {
        sumSubsidyAndPremiumCredit: useCallback(sumSubsidyAndPremiumCredit, []),
        checkForPendingApplication: useCallback(checkForPendingApplication, []),
        getTerminationBtnVisibility: useCallback(getTerminationBtnVisibility, []),
        getPayNowBtnVisibility: useCallback(getPayNowBtnVisibility, []),
        getDisEnrollBtnVisibility: useCallback(getDisEnrollBtnVisibility, []),
        payForPlan: useCallback(payForPlan, []),
        changeTerminationDate: useCallback(changeTerminationDate, []),
        getEnrollees: useCallback(getEnrollees, []),
        getTerminationDates: useCallback(getTerminationDates, []),
        getCancelBtnVisibility: useCallback(getCancelBtnVisibility, []),
        submitDisEnrollment: useCallback(submitDisEnrollment, [])
    };
};